import { Fragment, useCallback, useMemo } from "react"
import { Box, useTheme } from "@mui/material"

import { getIcon } from "utils/icon"
import {
  DEFAULT_FILTER_VALUE,
  SKELETON_COUNT,
  SKELETON_INDEX_TO_REF,
} from "constant"

import { useTranslation } from "providers/i18n"
import { useDining } from "hooks/dining/useDining"

import { PageLayout } from "layouts/PageLayout"

import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { Head } from "components/Head"

import { ImageSkeletonCard } from "components/ImageCard"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { DiningIcon } from "components/icons"
import { EmptyData } from "components/EmptyData"
import { VenueSelector } from "components/VenueSelector"
import { DiningCard } from "components/domains/dining/DiningCard"
import { IPageLayoutProps } from "layouts"

const DEFAULT_FILTER_ICON = <DiningIcon />

interface IDiningPageProps {
  Layout?: React.FC<IPageLayoutProps>
  AppNavigation?: React.ComponentType
}

export const DiningPage = ({
  Layout = PageLayout,
  AppNavigation,
}: IDiningPageProps) => {
  const {
    restaurants,
    currentCategory,
    categories,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    hasData,
    hasNextPage,
    endScrollRef,
    filterByCategoryId,
    filterByVenueId,
  } = useDining()

  const { t } = useTranslation({ defaultValue: null })

  const filterItems = useMemo(
    () => [
      {
        value: DEFAULT_FILTER_VALUE,
        label: t("All"),
        icon: DEFAULT_FILTER_ICON,
      },
      ...categories.map((category) => ({
        value: category.id,
        label: category.properties.name,
        icon: getIcon(category.properties) || DEFAULT_FILTER_ICON,
      })),
    ],
    [categories, t]
  )

  const handleChangeCategory = useCallback(
    (_, id) => {
      if (id === null) return
      filterByCategoryId(id)
    },
    [filterByCategoryId]
  )

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )

  const showFilterBar = filterItems.length > 1

  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}
  const {
    root,
    filterSection,
    filterContainer,
    diningImageCard,
    filterButton,
    gridContainer,
    emptyDataContainer,
    venueSelector,
    pageLayout,
  } = styleOverrides

  return (
    <>
      <Head title={t("Dining")} />
      <Layout sx={pageLayout} AppNavigation={AppNavigation}>
        <Box sx={root}>
          {showFilterBar && (
            <Box sx={filterSection}>
              <HorizontalScrollPane
                className="subCategory-filterbar"
                stackProps={{
                  sx: filterContainer,
                }}
              >
                <CategoryFilterBar
                  sx={filterButton}
                  activeCategory={currentCategory}
                  categories={categories}
                  onChange={handleChangeCategory}
                  renderFilterButton={CategoryFilterChip}
                />
              </HorizontalScrollPane>
            </Box>
          )}
          {hasData ? (
            <Box sx={gridContainer}>
              {restaurants.pages.map((page, pageNum) => {
                return (
                  <Fragment key={pageNum}>
                    {page.data.map((restaurant, index) => {
                      const isNewShop = restaurant?.properties?.isNewShop
                      const isComingSoon = restaurant?.properties?.isComingSoon

                      return (
                        <DiningCard
                          data={restaurant}
                          sx={diningImageCard}
                          key={`dining-${index}`}
                          isNewShop={isNewShop}
                          isComingSoon={isComingSoon}
                        />
                      )
                    })}
                  </Fragment>
                )
              })}
              {hasNextPage &&
                Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <ImageSkeletonCard
                    ref={index === SKELETON_INDEX_TO_REF ? endScrollRef : null}
                    key={index}
                  />
                ))}
            </Box>
          ) : (
            <Box sx={emptyDataContainer}>
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            </Box>
          )}
          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={filterByVenueId}
                currentVenue={locationVenueId}
              />
            </Box>
          )}
        </Box>
      </Layout>
    </>
  )
}
